import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, Pencil, Plug, Wifi, Eye, EyeOff, RefreshCw, Download,
  Trash2, CheckCircle2, XCircle, AlertTriangle, Clock, ToggleLeft,
  ExternalLink, Info,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { useSmartMovingIntegration } from '../../hooks/useSmartMovingIntegration';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';

const isDevMode = !IS_FIREBASE_CONFIGURED;

// ── Sample Data Viewer ──────────────────────────────────────────────────────

interface SampleDataProps {
  data: {
    leadsEndpoint: string;
    leadsCount: number;
    leads: Array<Record<string, unknown>>;
    jobsEndpoint: string;
    jobsCount: number;
    jobs: Array<Record<string, unknown>>;
  };
  sandbox?: boolean;
}

function SampleDataViewer({ data, sandbox }: SampleDataProps) {
  return (
    <div className="mt-2 bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-mist border-b border-stone">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
          {sandbox ? 'Mock API Response Preview' : 'API Response Preview'}
        </p>
        {sandbox && (
          <p className="text-[11px] text-slate mt-0.5">
            This is what SmartMoving&apos;s API returns. No real API call was made.
          </p>
        )}
      </div>

      {/* Leads */}
      <div className="px-4 py-3 border-b border-stone">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
            Leads / Opportunities
          </span>
          <span className="text-[11px] text-slate font-mono">
            {data.leadsCount} result{data.leadsCount !== 1 ? 's' : ''}
          </span>
        </div>
        <p className="text-[11px] text-slate font-mono mb-2 break-all">
          {data.leadsEndpoint}
        </p>
        {data.leads.length > 0 ? (
          <div className="flex flex-col gap-2">
            {data.leads.map((lead, i) => (
              <div key={i} className="bg-mist rounded-lg px-3 py-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] font-semibold text-midnight">
                    {(lead.firstName as string) ?? ''} {(lead.lastName as string) ?? ''}
                  </span>
                  <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full
                    ${(lead.status as string) === 'Booked' ? 'bg-emerald-50 text-emerald-700' :
                      (lead.status as string) === 'New' ? 'bg-blue-50 text-blue-700' :
                      'bg-amber-50 text-amber-700'}`}
                  >
                    {(lead.status as string) ?? 'Unknown'}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-0.5 text-[12px] text-slate">
                  {lead.companyName ? <p><span className="text-midnight font-medium">Company:</span> {String(lead.companyName)}</p> : null}
                  {lead.email ? <p><span className="text-midnight font-medium">Email:</span> {String(lead.email)}</p> : null}
                  {lead.phone ? <p><span className="text-midnight font-medium">Phone:</span> {String(lead.phone)}</p> : null}
                  {lead.serviceType ? <p><span className="text-midnight font-medium">Service:</span> {String(lead.serviceType)}</p> : null}
                  <p><span className="text-midnight font-medium">ID:</span> <span className="font-mono">{String(lead.id)}</span></p>
                  {lead.createdDate ? <p><span className="text-midnight font-medium">Created:</span> {new Date(String(lead.createdDate)).toLocaleDateString()}</p> : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-slate italic">No leads returned.</p>
        )}
      </div>

      {/* Jobs */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
            Jobs
          </span>
          <span className="text-[11px] text-slate font-mono">
            {data.jobsCount} result{data.jobsCount !== 1 ? 's' : ''}
          </span>
        </div>
        <p className="text-[11px] text-slate font-mono mb-2 break-all">
          {data.jobsEndpoint}
        </p>
        {data.jobs.length > 0 ? (
          <div className="flex flex-col gap-2">
            {data.jobs.map((job, i) => (
              <div key={i} className="bg-mist rounded-lg px-3 py-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] font-semibold text-midnight font-mono">
                    {(job.id as string) ?? ''}
                  </span>
                  <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full
                    ${(job.status as string) === 'Confirmed' ? 'bg-emerald-50 text-emerald-700' :
                      'bg-amber-50 text-amber-700'}`}
                  >
                    {(job.status as string) ?? 'Unknown'}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-0.5 text-[12px] text-slate">
                  {job.opportunityId ? <p><span className="text-midnight font-medium">Lead ID:</span> <span className="font-mono">{String(job.opportunityId)}</span></p> : null}
                  {job.moveDate ? <p><span className="text-midnight font-medium">Move Date:</span> {new Date(String(job.moveDate)).toLocaleDateString()}</p> : null}
                  {job.crewSize != null ? <p><span className="text-midnight font-medium">Crew Size:</span> {String(job.crewSize)}</p> : null}
                  {job.estimatedHours != null ? <p><span className="text-midnight font-medium">Est. Hours:</span> {String(job.estimatedHours)}</p> : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] text-slate italic">No jobs returned.</p>
        )}
      </div>

      {/* Raw JSON toggle */}
      <RawJsonSection leads={data.leads} jobs={data.jobs} />
    </div>
  );
}

function RawJsonSection({ leads, jobs }: { leads: Array<Record<string, unknown>>; jobs: Array<Record<string, unknown>> }) {
  const [show, setShow] = useState(false);
  return (
    <div className="border-t border-stone">
      <button
        type="button"
        onClick={() => setShow(v => !v)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-slate hover:text-midnight transition-colors"
      >
        Raw JSON
        {show ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {show && (
        <pre className="px-4 pb-3 text-[11px] text-midnight font-mono whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
{JSON.stringify({ leads, jobs }, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ── Connect Form ───────────────────────────────────────────────────────────

interface ConnectFormProps {
  orgId: string;
  onConnected: () => void;
}

function ConnectForm({ orgId, onConnected }: ConnectFormProps) {
  const { saveConfig, testConnection } = useSmartMovingIntegration(orgId);
  const [apiKey, setApiKey] = useState('');
  const [providerKey, setProviderKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean; message: string; sandbox?: boolean;
    sampleData?: { leadsEndpoint: string; leadsCount: number; leads: Array<Record<string, unknown>>;
      jobsEndpoint: string; jobsCount: number; jobs: Array<Record<string, unknown>> };
  } | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [error, setError] = useState('');

  async function handleTest() {
    if (!apiKey.trim()) { setError('API Key is required'); return; }
    setError('');
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testConnection(apiKey.trim(), baseUrl.trim() || undefined);
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, message: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    if (!apiKey.trim()) { setError('API Key is required'); return; }
    setError('');
    setSaving(true);
    try {
      await saveConfig({
        enabled: true,
        apiKey: apiKey.trim(),
        providerKey: providerKey.trim() || undefined,
        baseUrl: baseUrl.trim() || undefined,
        syncEnabled: false,
        syncIntervalMinutes: 15,
        syncFilters: { serviceTypes: [], statuses: [] },
        fieldMapping: {},
        pushBackEnabled: false,
      });
      onConnected();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs font-semibold text-secondary-theme uppercase tracking-wider">Connect SmartMoving</p>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="sm-api-key" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
          API Key <span className="text-rose">*</span>
        </label>
          <input
            id="sm-api-key"
            type="password"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="Enter your SmartMoving Open API key"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
          <p className="text-[11px] text-slate mt-1">
            No account? Type <span className="font-mono font-bold text-plum">sandbox</span> to test with mock data.
          </p>
        </div>

        <div>
          <label htmlFor="sm-provider-key" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
            Provider Key <span className="text-[11px] text-slate font-normal">(optional)</span>
          </label>
          <input
            id="sm-provider-key"
            type="password"
            value={providerKey}
            onChange={e => setProviderKey(e.target.value)}
            placeholder="Lead API provider key (for push-back)"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        <div>
          <label htmlFor="sm-base-url" className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
            Base URL <span className="text-[11px] text-slate font-normal">(optional)</span>
          </label>
          <input
            id="sm-base-url"
            type="url"
            value={baseUrl}
            onChange={e => setBaseUrl(e.target.value)}
            placeholder="https://api.smartmoving.com"
            className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                       text-small text-midnight placeholder:text-slate
                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                       transition-colors duration-150"
          />
        </div>

        {testResult && (
          <div className="flex flex-col gap-0">
            <div className={`px-3 py-2 rounded-lg text-[13px] flex items-center gap-2
              ${testResult.success
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                : 'bg-rose/10 border border-rose/30 text-rose'}`}
            >
              {testResult.success
                ? <CheckCircle2 size={16} className="shrink-0" />
                : <XCircle size={16} className="shrink-0" />}
              <span className="flex-1">{testResult.message}</span>
              {testResult.sampleData && (
                <button
                  type="button"
                  onClick={() => setShowDetail(v => !v)}
                  className="shrink-0 ml-1 text-current hover:opacity-70 transition-opacity"
                  aria-label={showDetail ? 'Hide details' : 'Show details'}
                >
                  {showDetail ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              )}
            </div>
            {testResult.sampleData && showDetail && (
              <SampleDataViewer data={testResult.sampleData} sandbox={testResult.sandbox} />
            )}
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !apiKey.trim()}
            className="flex-1 h-11 rounded-lg border border-stone text-small text-slate font-semibold
                       hover:border-plum hover:text-plum active:bg-mist disabled:opacity-50
                       transition-colors duration-150 flex items-center justify-center gap-2"
          >
            {testing ? <LoadingSpinner size="sm" /> : <Wifi size={16} />}
            Test Connection
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !apiKey.trim()}
            className="flex-1 h-11 rounded-lg bg-plum text-white text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150 flex items-center justify-center gap-2"
          >
            {saving ? 'Saving…' : 'Connect'}
          </button>
        </div>
    </div>
  );
}

// ── Connected View ──────────────────────────────────────────────────────────

interface ConnectedViewProps {
  orgId: string;
}

function ConnectedView({ orgId }: ConnectedViewProps) {
  const {
    config, syncLog, loading, error,
    saveConfig, testConnection, triggerSync, disconnect,
    refetch, refetchSyncLog,
  } = useSmartMovingIntegration(orgId);

  const [showApiKey, setShowApiKey] = useState(false);
  const [showProviderKey, setShowProviderKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean; message: string; sandbox?: boolean;
    sampleData?: { leadsEndpoint: string; leadsCount: number; leads: Array<Record<string, unknown>>;
      jobsEndpoint: string; jobsCount: number; jobs: Array<Record<string, unknown>> };
  } | null>(null);
  const [showTestDetail, setShowTestDetail] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ imported: number; skipped: number; errors: number } | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [updatingSync, setUpdatingSync] = useState(false);
  const [updatingPushBack, setUpdatingPushBack] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-small text-rose">{error}</p>
      </div>
    );
  }

  if (!config) return null;

  const isSandbox = config.apiKey === '••••••••' || config.apiKey.toLowerCase().includes('sandbox');

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testConnection();
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, message: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleToggleSync() {
    if (!config) return;
    setUpdatingSync(true);
    try {
      await saveConfig({ ...config, syncEnabled: !config.syncEnabled });
      await refetch();
    } catch {
      // handled
    } finally {
      setUpdatingSync(false);
    }
  }

  async function handleTogglePushBack() {
    if (!config) return;
    setUpdatingPushBack(true);
    try {
      await saveConfig({ ...config, pushBackEnabled: !config.pushBackEnabled });
      await refetch();
    } catch {
      // handled
    } finally {
      setUpdatingPushBack(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    setSyncError(null);
    try {
      const result = await triggerSync();
      setSyncResult({ imported: result.imported, skipped: result.skipped, errors: result.errors });
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      await disconnect();
    } catch {
      setDisconnecting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Connection Status */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700">
            <Wifi size={12} /> Connected
          </span>
        </div>

        {isSandbox && (
          <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[13px] text-amber-800 flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="shrink-0" />
            <span><strong>Sandbox mode</strong> — using mock data, not a real SmartMoving account.</span>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {/* API Key */}
          <div className="flex items-center justify-between">
            <span className="text-[13px] text-slate">API Key</span>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-mono text-midnight">
                {showApiKey ? config.apiKey : config.apiKey}
              </span>
              <button
                type="button"
                onClick={() => setShowApiKey(v => !v)}
                className="text-slate hover:text-midnight transition-colors"
                aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
              >
                {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {/* Provider Key */}
          {config.providerKey && (
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-slate">Provider Key</span>
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-mono text-midnight">
                  {showProviderKey ? config.providerKey : config.providerKey}
                </span>
                <button
                  type="button"
                  onClick={() => setShowProviderKey(v => !v)}
                  className="text-slate hover:text-midnight transition-colors"
                  aria-label={showProviderKey ? 'Hide provider key' : 'Show provider key'}
                >
                  {showProviderKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          )}

          {/* Base URL */}
          {config.baseUrl && (
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-slate">Base URL</span>
              <span className="text-[13px] text-midnight">{config.baseUrl}</span>
            </div>
          )}

          {/* Last Sync */}
          <div className="flex items-center justify-between pt-2 border-t border-stone">
            <span className="text-[13px] text-slate">Last Sync</span>
            <span className="text-[13px] text-midnight flex items-center gap-1.5">
              {config.lastSyncAt ? (
                <>
                  {config.lastSyncStatus === 'success' && <CheckCircle2 size={14} className="text-emerald-500" />}
                  {config.lastSyncStatus === 'error' && <XCircle size={14} className="text-rose" />}
                  {new Date(config.lastSyncAt).toLocaleString()}
                </>
              ) : (
                <span className="text-slate italic">Never synced</span>
              )}
            </span>
          </div>

          {config.lastSyncError && (
            <div className="px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              {config.lastSyncError}
            </div>
          )}
        </div>
      </div>

      {/* Auto-Sync Toggle */}
      <div className="flex items-center justify-between border border-theme rounded-lg px-4 py-3.5">
        <div>
          <p className="text-[13px] font-semibold text-midnight">Auto-Sync</p>
          <p className="text-[12px] text-slate mt-0.5 leading-snug">
            Automatically import leads every {config.syncIntervalMinutes} minutes.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={config.syncEnabled}
          disabled={updatingSync}
          onClick={handleToggleSync}
          className={`relative shrink-0 ml-4 w-11 h-6 rounded-full transition-colors duration-200
            ${config.syncEnabled ? 'bg-plum' : 'bg-stone'}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
              ${config.syncEnabled ? 'translate-x-5' : 'translate-x-0'}`}
          />
        </button>
      </div>

      {/* Sync Now */}
      <button
        type="button"
        onClick={handleSync}
        disabled={syncing}
        className="w-full h-11 rounded-lg bg-plum text-white text-small font-semibold
                   hover:bg-rose active:opacity-80 disabled:opacity-50
                   transition-colors duration-150 flex items-center justify-center gap-2"
      >
        {syncing ? <LoadingSpinner size="sm" /> : <Download size={16} />}
        {syncing ? 'Syncing…' : 'Sync Now'}
      </button>

      {syncResult && (
        <div className={`px-3 py-2 rounded-lg text-[13px] flex items-center gap-2
          ${syncResult.errors > 0 && syncResult.imported === 0
            ? 'bg-rose/10 border border-rose/30 text-rose'
            : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}
        >
          {syncResult.errors > 0 && syncResult.imported === 0
            ? <XCircle size={16} />
            : <CheckCircle2 size={16} />}
          <span>
            {syncResult.imported} imported, {syncResult.skipped} skipped
            {syncResult.errors > 0 && `, ${syncResult.errors} errors`}
          </span>
        </div>
      )}

      {syncError && (
        <div className="px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose flex items-center gap-2">
          <XCircle size={16} />
          {syncError}
        </div>
      )}

      {/* Push-Back Toggle */}
      <div className="flex items-center justify-between border border-theme rounded-lg px-4 py-3.5">
        <div>
          <p className="text-[13px] font-semibold text-midnight">Push-Back to SmartMoving</p>
          <p className="text-[12px] text-slate mt-0.5 leading-snug">
            Send quote updates back to SmartMoving via the Lead API.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={config.pushBackEnabled}
          disabled={updatingPushBack}
          onClick={handleTogglePushBack}
          className={`relative shrink-0 ml-4 w-11 h-6 rounded-full transition-colors duration-200
            ${config.pushBackEnabled ? 'bg-plum' : 'bg-stone'}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
              ${config.pushBackEnabled ? 'translate-x-5' : 'translate-x-0'}`}
          />
        </button>
      </div>

      {/* Test Connection */}
      <button
        type="button"
        onClick={handleTest}
        disabled={testing}
        className="w-full h-11 rounded-lg border border-stone text-small text-slate font-semibold
                   hover:border-plum hover:text-plum active:bg-mist disabled:opacity-50
                   transition-colors duration-150 flex items-center justify-center gap-2"
      >
        {testing ? <LoadingSpinner size="sm" /> : <RefreshCw size={16} />}
        Test Connection
      </button>

      {testResult && (
        <div className="flex flex-col gap-0">
          <div className={`px-3 py-2 rounded-lg text-[13px] flex items-center gap-2
            ${testResult.success
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-rose/10 border border-rose/30 text-rose'}`}
          >
            {testResult.success
              ? <CheckCircle2 size={16} className="shrink-0" />
              : <XCircle size={16} className="shrink-0" />}
            <span className="flex-1">{testResult.message}</span>
            {testResult.sampleData && (
              <button
                type="button"
                onClick={() => setShowTestDetail(v => !v)}
                className="shrink-0 ml-1 text-current hover:opacity-70 transition-opacity"
                aria-label={showTestDetail ? 'Hide details' : 'Show details'}
              >
                {showTestDetail ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            )}
          </div>
          {testResult.sampleData && showTestDetail && (
            <SampleDataViewer data={testResult.sampleData} sandbox={testResult.sandbox} />
          )}
        </div>
      )}

      {/* Sync Log */}
      {syncLog.length > 0 && (
        <div className="border border-theme rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-subtle border-b border-theme flex items-center gap-2">
            <Clock size={16} className="text-plum" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
              Recent Sync Activity
            </span>
            <button
              type="button"
              onClick={refetchSyncLog}
              className="ml-auto text-slate hover:text-plum transition-colors"
              aria-label="Refresh sync log"
            >
              <RefreshCw size={14} />
            </button>
          </div>

          <div className="divide-y divide-stone max-h-64 overflow-y-auto">
            {syncLog.slice(0, 20).map(entry => (
              <div key={entry.id} className="px-4 py-2.5 flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  {entry.status === 'success' && <CheckCircle2 size={14} className="text-emerald-500" />}
                  {entry.status === 'error' && <XCircle size={14} className="text-rose" />}
                  {entry.status === 'skipped' && <AlertTriangle size={14} className="text-amber-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-midnight leading-snug">{entry.details}</p>
                  {entry.error && (
                    <p className="text-[12px] text-rose mt-0.5">{entry.error}</p>
                  )}
                  <p className="text-[11px] text-slate mt-0.5">
                    {new Date(entry.timestamp).toLocaleString()} · {entry.direction} · {entry.action}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disconnect */}
      {!confirmDisconnect ? (
        <button
          type="button"
          onClick={() => setConfirmDisconnect(true)}
          className="w-full h-11 rounded-lg border border-rose/30 text-small text-rose font-semibold
                     hover:bg-rose/5 active:bg-rose/10
                     transition-colors duration-150 flex items-center justify-center gap-2"
        >
          <Trash2 size={16} />
          Disconnect SmartMoving
        </button>
      ) : (
        <div className="bg-rose/5 border border-rose/30 rounded-xl p-4">
          <p className="text-[13px] text-midnight font-semibold mb-2">
            Are you sure you want to disconnect?
          </p>
          <p className="text-[12px] text-slate mb-3">
            This will remove the API keys and sync history. You can reconnect later.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setConfirmDisconnect(false)}
              className="flex-1 h-10 rounded-lg border border-stone text-small text-slate font-semibold
                         hover:border-midnight hover:text-midnight active:bg-mist
                         transition-colors duration-150"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="flex-1 h-10 rounded-lg bg-rose text-white text-small font-semibold
                         hover:bg-rose/80 active:opacity-80 disabled:opacity-50
                         transition-colors duration-150 flex items-center justify-center gap-2"
            >
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Feature Toggle Definitions ──────────────────────────────────────────────

/** UI feature definitions. "Operations" is a single toggle that controls Dispatch + Jobs + Fleet. */
const FEATURE_TOGGLES: Array<{
  label: string;
  desc: string;
  defaultOn: boolean;
  /** feature* keys to read/write for this toggle. Multiple keys = grouped toggle. */
  keys: Array<{ key: string; allowedKey: string; defaultOn: boolean }>;
  /** Optional link shown below the toggle when it's enabled. */
  linkWhenEnabled?: { label: string; to: string };
}> = [
  { label: 'Commercial Moving Quotes', desc: 'Commercial moving quote functionality.', defaultOn: true, keys: [{ key: 'featureCommercialMovingQuotes', allowedKey: 'allowedFeatureCommercialMovingQuotes', defaultOn: true }] },
  { label: 'Residential Moving Quotes', desc: 'Residential moving quote functionality.', defaultOn: false, keys: [{ key: 'featureResidentialMovingQuotes', allowedKey: 'allowedFeatureResidentialMovingQuotes', defaultOn: false }] },
  {
    label: 'Operations',
    desc: 'Dispatch, Jobs, and Fleet Management — all operations features.',
    defaultOn: true,
    keys: [
      { key: 'featureDispatch', allowedKey: 'allowedFeatureDispatch', defaultOn: true },
      { key: 'featureJobs', allowedKey: 'allowedFeatureJobs', defaultOn: true },
      { key: 'featureFleetManagement', allowedKey: 'allowedFeatureFleetManagement', defaultOn: false },
    ],
  },
  { label: 'Lead Pipeline', desc: 'Leads page, lead tracking, and follow-up management.', defaultOn: false, keys: [{ key: 'featureLeadPipeline', allowedKey: 'allowedFeatureLeadPipeline', defaultOn: false }] },
  {
    label: 'Customer-facing Online Features',
    desc: 'Client portal for online quotes and public booking form for capturing leads.',
    defaultOn: false,
    keys: [
      { key: 'featureClientPortal', allowedKey: 'allowedFeatureClientPortal', defaultOn: true },
      { key: 'featureOnlineLeadCapture', allowedKey: 'allowedFeatureOnlineLeadCapture', defaultOn: false },
    ],
    linkWhenEnabled: { label: 'Configure Online Leads & Quotes', to: '/admin/customer-facing/booking-form' },
  },
  { label: 'Quote PDFs', desc: 'Generate PDFs when creating quote versions.', defaultOn: true, keys: [{ key: 'featureQuotePdfs', allowedKey: 'allowedFeatureQuotePdfs', defaultOn: true }] },
  { label: 'Customer Payments', desc: 'Configure and accept payments from customers.', defaultOn: false, keys: [{ key: 'featureCustomerPayments', allowedKey: 'allowedFeatureCustomerPayments', defaultOn: false }], linkWhenEnabled: { label: 'Configure Customer Payments', to: '/admin/payments/payment-methods' } },
  { label: 'SmartMoving Integration', desc: 'Connect and sync with SmartMoving CRM.', defaultOn: false, keys: [{ key: 'featureSmartMovingIntegration', allowedKey: 'allowedFeatureSmartMovingIntegration', defaultOn: false }] },
];

// ── Features Card ──────────────────────────────────────────────────────────

function FeaturesCard({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { org, updateSettings } = useOrgSettings(orgId);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState<{ label: string; keys: Array<{ key: string }> } | null>(null);

  if (!org) return null;

  const orgData = org as unknown as Record<string, unknown>;

  // A feature group is visible if ANY of its allowed keys is not false (or dev mode)
  const visibleFeatures = FEATURE_TOGGLES.filter(f =>
    f.keys.some(k => {
      const allowed = orgData[k.allowedKey];
      return isDevMode || allowed !== false;
    }),
  );

  if (visibleFeatures.length === 0) return null;

  /** Get the allowed keys for a feature group (only keys whose allowedKey is not false). */
  function getAllowedKeys(f: typeof FEATURE_TOGGLES[number]) {
    return f.keys.filter(k => isDevMode || orgData[k.allowedKey] !== false);
  }

  function getGroupValue(f: typeof FEATURE_TOGGLES[number]): boolean {
    const allowed = getAllowedKeys(f);
    if (allowed.length === 0) return false;
    // A grouped toggle is ON if ALL its allowed sub-keys are on
    return allowed.every(k =>
      k.defaultOn ? orgData[k.key] !== false : (orgData[k.key] ?? false) as boolean,
    );
  }

  async function handleToggle(f: typeof FEATURE_TOGGLES[number], currentValue: boolean) {
    const allowed = getAllowedKeys(f);
    if (currentValue) {
      // Disabling — show confirmation
      setConfirmDisable({ label: f.label, keys: allowed });
      return;
    }
    await doToggle(allowed, false);
  }

  async function doToggle(keys: Array<{ key: string }>, currentValue: boolean) {
    const toggleId = keys.map(k => k.key).join(',');
    setTogglingKey(toggleId);
    setError('');
    try {
      const patch: Record<string, boolean> = {};
      for (const k of keys) patch[k.key] = !currentValue;
      await updateSettings(patch);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update feature');
    } finally {
      setTogglingKey(null);
    }
  }

  async function handleConfirmDisable() {
    if (!confirmDisable) return;
    await doToggle(confirmDisable.keys, true);
    setConfirmDisable(null);
  }

  return (
    <>
      <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3 bg-mist border-b border-stone flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ToggleLeft size={16} className="text-plum" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
              Features
            </span>
          </div>
          <button
            type="button"
            onClick={() => setEditing(v => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-semibold
                       text-slate hover:text-midnight hover:bg-white/60 transition-colors"
          >
            <Pencil size={12} />
            {editing ? 'Done' : 'Edit'}
          </button>
        </div>

        {error && (
          <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose">
            {error}
          </div>
        )}

        <div className="divide-y divide-stone">
          {visibleFeatures.map(f => {
            const value = getGroupValue(f);
            const allowedKeys = getAllowedKeys(f);
            const toggleId = allowedKeys.map(k => k.key).join(',');
            const isToggling = togglingKey === toggleId;
            return (
              <div key={f.label} className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-semibold text-midnight">{f.label}</p>
                    <p className="text-[12px] text-slate mt-0.5 leading-snug">{f.desc}</p>
                  </div>
                  {editing ? (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={value}
                      disabled={isToggling}
                      onClick={() => handleToggle(f, value)}
                      className={`relative shrink-0 ml-4 w-11 h-6 rounded-full transition-colors duration-200
                        ${value ? 'bg-plum' : 'bg-stone'} ${isToggling ? 'opacity-50' : ''}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                          ${value ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  ) : (
                    <div
                      aria-disabled="true"
                      className={`relative shrink-0 ml-4 w-11 h-6 rounded-full opacity-60
                        ${value ? 'bg-plum' : 'bg-stone'}`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow
                          ${value ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </div>
                  )}
                </div>
                {f.linkWhenEnabled && value && (
                  <button
                    type="button"
                    onClick={() => navigate(f.linkWhenEnabled!.to)}
                    className="mt-2 text-[12px] text-plum font-medium hover:underline flex items-center gap-1"
                  >
                    {f.linkWhenEnabled.label}
                    <ExternalLink size={10} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Disable confirmation dialog */}
      {confirmDisable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-midnight/50" onClick={() => setConfirmDisable(null)} aria-hidden="true" />
          <div className="relative bg-white rounded-2xl px-5 py-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-amber-500 shrink-0" />
              <h3 className="font-display text-[16px] font-semibold text-midnight">Disable {confirmDisable.label}?</h3>
            </div>
            <p className="text-[13px] text-slate leading-relaxed mb-5">
              No data will be removed or deleted. The application features for <strong>{confirmDisable.label}</strong> will
              be hidden from your team. Enabling this feature again in the future will restore all data.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDisable(null)}
                className="flex-1 h-10 rounded-lg border border-stone text-[13px] font-semibold text-slate
                           hover:border-midnight hover:text-midnight transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDisable}
                className="flex-1 h-10 rounded-lg bg-amber-500 text-white text-[13px] font-semibold
                           hover:bg-amber-600 transition-colors"
              >
                Disable
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

// ── SmartMoving Toggle (for Integrations page) ─────────────────────────────

function SmartMovingToggle({ orgId }: { orgId: string }) {
  const { org, updateSettings } = useOrgSettings(orgId);
  const [toggling, setToggling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!org) return null;

  const orgData = org as unknown as Record<string, unknown>;
  const allowed = isDevMode || orgData.allowedFeatureSmartMovingIntegration !== false;
  if (!allowed) return null;

  const enabled = orgData.featureSmartMovingIntegration as boolean ?? false;

  async function doToggle(newValue: boolean) {
    setToggling(true);
    setShowConfirm(false);
    try {
      await updateSettings({ featureSmartMovingIntegration: newValue });
    } finally {
      setToggling(false);
    }
  }

  function handleToggle() {
    if (enabled) {
      setShowConfirm(true);
    } else {
      doToggle(true);
    }
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-primary-theme font-medium">Integrate with SmartMoving</p>
          <p className="text-xs text-secondary-theme mt-0.5">
            {enabled
              ? 'SmartMoving integration is active. Connect your account below.'
              : 'Enable to connect and sync with SmartMoving CRM.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={toggling}
          onClick={handleToggle}
          className={`relative shrink-0 ml-4 w-12 h-7 rounded-full transition-colors duration-200
            ${enabled ? 'bg-plum' : 'bg-subtle'} ${toggling ? 'opacity-50' : ''}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200
              ${enabled ? 'translate-x-5' : 'translate-x-0'}`}
          />
        </button>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-midnight/50" onClick={() => setShowConfirm(false)} aria-hidden="true" />
          <div className="relative bg-white rounded-2xl px-5 py-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-amber-500 shrink-0" />
              <h3 className="font-display text-[16px] font-semibold text-midnight">Disable SmartMoving?</h3>
            </div>
            <p className="text-[13px] text-slate leading-relaxed mb-5">
              Your SmartMoving connection data will be preserved, but syncing will stop. You can re-enable at any time.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="flex-1 h-10 rounded-lg border border-stone text-[13px] font-semibold text-slate
                           hover:border-midnight hover:text-midnight transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => doToggle(false)}
                className="flex-1 h-10 rounded-lg bg-amber-500 text-white text-[13px] font-semibold
                           hover:bg-amber-600 transition-colors"
              >
                Disable
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export function IntegrationsPage({ view }: { view?: 'features' | 'integrations' } = {}) {
  const navigate = useNavigate();
  const { adminOrg, loading: profileLoading } = useUserProfile();
  const orgId = adminOrg?.orgId;
  const { org: orgSettings } = useOrgSettings(orgId ?? '');

  const smartMovingEnabled = isDevMode || (orgSettings?.featureSmartMovingIntegration ?? false);

  const {
    config, loading,
  } = useSmartMovingIntegration(orgId ?? '');

  const [justConnected, setJustConnected] = useState(false);

  const isConnected = config !== null || justConnected;

  if (profileLoading || !orgId) {
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-3xl lg:max-w-5xl md:mx-auto">
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  const pageTitle = view === 'features' ? 'Features' : view === 'integrations' ? 'Integrations' : 'Features & Integrations';
  const pageDesc = view === 'features'
    ? 'Enable or disable platform capabilities.'
    : view === 'integrations'
      ? 'SmartMoving and third-party connections.'
      : 'Manage platform features and third-party integrations.';

  const showFeatures = !view || view === 'features';
  const showIntegrations = !view || view === 'integrations';

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-3xl lg:max-w-5xl md:mx-auto">
      {/* Breadcrumbs */}
      {view && (
        <nav className="flex items-center gap-1.5 text-xs text-secondary-theme mb-2">
          <button type="button" onClick={() => navigate('/admin')} className="hover:text-plum transition-colors">
            Admin Panel
          </button>
          <span>&gt;</span>
          <button type="button" onClick={() => navigate('/admin/platform')} className="hover:text-plum transition-colors">
            Platform
          </button>
        </nav>
      )}

      <div className="flex items-center gap-3 mb-6">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-plum/10 flex items-center justify-center">
          {view === 'integrations' ? <Plug size={20} className="text-plum" /> : <ToggleLeft size={20} className="text-plum" />}
        </div>
        <div>
          <h1 className="font-display text-h1 text-midnight">{pageTitle}</h1>
          <p className="text-small text-slate">{pageDesc}</p>
        </div>
      </div>

      {showFeatures && (
        <div className="mb-5">
          <FeaturesCard orgId={orgId} />
        </div>
      )}

      {showIntegrations && (
        <div className="space-y-5">
          {/* SmartMoving card — always show the toggle */}
          <section className="bg-surface border border-theme rounded-xl p-4 shadow-brand-sm space-y-4">
            <div className="flex items-center gap-2">
              <Plug size={16} className="text-plum" />
              <h2 className="text-sm font-semibold text-primary-theme">SmartMoving</h2>
            </div>

            <SmartMovingToggle orgId={orgId} />

            {smartMovingEnabled && (
              <>
                {loading && !isConnected && (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="lg" />
                  </div>
                )}

                {!loading && !isConnected && (
                  <div className="border-t border-theme pt-4">
                    <ConnectForm orgId={orgId} onConnected={() => setJustConnected(true)} />
                  </div>
                )}

                {isConnected && (
                  <div className="border-t border-theme pt-4">
                    <ConnectedView orgId={orgId} />
                  </div>
                )}
              </>
            )}
          </section>

          {/* Empty state — when SmartMoving toggle is not allowed (no integrations visible) */}
          {!isDevMode && (orgSettings as unknown as Record<string, unknown>)?.allowedFeatureSmartMovingIntegration === false && (
            <div className="bg-surface border border-theme rounded-xl p-6 text-center shadow-brand-sm">
              <div className="flex justify-center mb-3">
                <Info size={24} className="text-secondary-theme" />
              </div>
              <p className="text-sm text-primary-theme font-medium mb-1">No integrations available</p>
              <p className="text-xs text-secondary-theme">
                There are no integrations currently enabled for your organization. Contact IOMI to enable integrations.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
